import { is3dModelType } from './model-3d.js';

export { MEDIA_TYPE_3D_MODEL } from './model-3d.js';
export { is3dModelType };

export const MEDIA_TYPE_YOUTUBE = 'YOUTUBE';

export const MOBILE_GALLERY_MAX_WIDTH = 767;

export const THUMB_SLIDE_GAP_PX = 8;

export function readIsMobileGallery() {
	if (typeof globalThis.matchMedia !== 'function') {
		return false;
	}

	return globalThis.matchMedia(`(max-width: ${MOBILE_GALLERY_MAX_WIDTH}px)`).matches;
}

/**
 * @param {string | undefined} type
 */
export function isYoutubeType(type) {
	return type === MEDIA_TYPE_YOUTUBE;
}

/**
 * @param {{ id?: string }} item
 * @returns {string}
 */
function getYouTubeVideoId(item) {
	return typeof item.id === 'string' ? item.id : '';
}

/**
 * @param {{ caption?: string }} item
 * @returns {string}
 */
function getItemCaption(item) {
	return typeof item.caption === 'string' ? item.caption : '';
}

/**
 * @param {{
 *   main?: string,
 *   preview?: string,
 *   thumb?: string,
 * }} item
 */
export function getThumbUrl(item) {
	if (typeof item.thumb === 'string' && item.thumb !== '') {
		return item.thumb;
	}

	if (typeof item.preview === 'string' && item.preview !== '') {
		return item.preview;
	}

	return typeof item.main === 'string' ? item.main : '';
}

/**
 * Hero uses the largest available image (same resolution as the lightbox).
 * @param {{
 *   main?: string,
 *   preview?: string,
 *   thumb?: string,
 * }} item
 */
export function getHeroImageUrl(item) {
	return getLightboxUrl(item);
}

/**
 * @param {{
 *   main?: string,
 *   preview?: string,
 *   thumb?: string,
 * }} item
 */
export function getLightboxUrl(item) {
	if (typeof item.main === 'string' && item.main !== '') {
		return item.main;
	}

	if (typeof item.preview === 'string' && item.preview !== '') {
		return item.preview;
	}

	return typeof item.thumb === 'string' ? item.thumb : '';
}

/**
 * @param {Array<unknown>} items
 */
export function hasMultipleMedia(items) {
	return items.length > 1;
}

/**
 * @param {{ type?: string, id?: string, main?: string, preview?: string, thumb?: string, modelUrl?: string }} item
 */
export function isLightboxNavigableItem(item) {
	if (is3dModelType(item.type)) {
		return false;
	}

	if (isYoutubeType(item.type)) {
		return getYouTubeVideoId(item) !== '';
	}

	return getLightboxUrl(item) !== '';
}

/**
 * @param {{ modelUrl?: string }} item
 * @returns {string}
 */
export function getModelUrl(item) {
	return typeof item.modelUrl === 'string' ? item.modelUrl : '';
}

/**
 * Indexes of gallery items that can be shown in the lightbox (images and YouTube).
 * @param {Array<{ type?: string, id?: string, main?: string, preview?: string, thumb?: string }>} items
 * @returns {number[]}
 */
export function getLightboxNavigableIndexes(items) {
	const navigableIndexes = [];

	for (let index = 0; index < items.length; index += 1) {
		const item = items[index];

		if (!isLightboxNavigableItem(item)) {
			continue;
		}

		navigableIndexes.push(index);
	}

	return navigableIndexes;
}

/**
 * Maps a gallery slide index to the corresponding YARL lightbox slide index.
 * @param {Array<{ type?: string, id?: string, main?: string, preview?: string, thumb?: string }>} items
 * @param {number} galleryIndex
 * @returns {number}
 */
export function getLightboxIndexForGalleryIndex(items, galleryIndex) {
	const navigableIndexes = getLightboxNavigableIndexes(items);
	const lightboxIndex = navigableIndexes.indexOf(galleryIndex);

	return lightboxIndex === -1 ? 0 :lightboxIndex;
}

/**
 * Maps a YARL lightbox slide index back to the gallery slide index.
 * @param {Array<{ type?: string, id?: string, main?: string, preview?: string, thumb?: string }>} items
 * @param {number} lightboxIndex
 * @returns {number}
 */
export function getGalleryIndexForLightboxIndex(items, lightboxIndex) {
	const navigableIndexes = getLightboxNavigableIndexes(items);
	const galleryIndex = navigableIndexes[lightboxIndex];

	if (galleryIndex === undefined) {
		return navigableIndexes[0] ?? 0;
	}

	return galleryIndex;
}

/**
 * @param {string} videoId
 */
export function buildYouTubeEmbedUrl(videoId) {
	const params = new URLSearchParams({
		iv_load_policy: '3',
		modestbranding: '1',
		rel: '0',
	});

	return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
}

/**
 * @param {{
 *   type?: string,
 *   id?: string,
 *   caption?: string,
 *   main?: string,
 *   preview?: string,
 *   thumb?: string,
 * }} item
 * @param {string} imageAlt
 */
function buildLightboxSlide(item, imageAlt) {
	const caption = getItemCaption(item);

	if (isYoutubeType(item.type)) {
		return {
			description: caption,
			height: 720,
			type: 'youtube',
			videoId: getYouTubeVideoId(item),
			width: 1280,
		};
	}

	return {
		alt: imageAlt,
		description: caption,
		src: getLightboxUrl(item),
	};
}

/**
 * Builds YARL slide objects for all lightbox-navigable gallery items.
 * @param {Array<{ type?: string, id?: string, caption?: string, main?: string, preview?: string, thumb?: string }>} items
 * @param {string} imageAlt
 * @returns {Array<Record<string, unknown>>}
 */
export function buildProductMediaLightboxSlides(items, imageAlt) {
	const navigableIndexes = getLightboxNavigableIndexes(items);
	const slides = [];

	for (const galleryIndex of navigableIndexes) {
		const item = items[galleryIndex];

		if (item === undefined) {
			continue;
		}

		slides.push(buildLightboxSlide(item, imageAlt));
	}

	return slides;
}

/**
 * @param {string} moduleId
 * @param {number} index
 */
export function youtubeContainerId(moduleId, index) {
	const safeModuleId = moduleId.replace(/[^a-zA-Z0-9_-]/g, '_');

	return `product_image_video_${safeModuleId}_${index}`;
}

/**
 * Registers a callback for the YouTube iframe API ready event, chaining any existing handler.
 * @param {() => void} callback
 * @returns {() => void} unregisters this callback and restores the prior handler when still active
 */
export function registerYouTubeIframeApiReady(callback) {
	const previousReadyHandler = globalThis.onYouTubeIframeAPIReady;

	function onYouTubeReady() {
		if (typeof previousReadyHandler === 'function') {
			previousReadyHandler();
		}

		callback();
	}

	globalThis.onYouTubeIframeAPIReady = onYouTubeReady;

	return function unregisterYouTubeIframeApiReady() {
		if (globalThis.onYouTubeIframeAPIReady === onYouTubeReady) {
			globalThis.onYouTubeIframeAPIReady = previousReadyHandler;
		}
	};
}
